/**
 * Výběr období s presety a porovnávacím obdobím (spec 08 §2).
 * Stav jde do URL přes useUrlFilters — deep-link musí přežít refresh.
 */
import { useState } from 'react';
import {
  COMPARE_LABELS,
  PRESET_LABELS,
  compareRange,
  detectPreset,
  formatRange,
  presetRange,
  type CompareMode,
  type DateRange,
  type RangePreset,
} from '@/lib/dates';

export interface DateRangePickerProps {
  range: DateRange;
  compare: CompareMode;
  onChange: (range: DateRange, compare: CompareMode) => void;
}

const PRESETS: RangePreset[] = ['today', 'yesterday', '7d', '30d', '90d', 'mtd'];

export function DateRangePicker({ range, compare, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const activePreset = detectPreset(range);
  const comparison = compareRange(range, compare);

  return (
    <div className="date-range">
      <button
        type="button"
        className="date-range__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
          <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
          <path d="M2 6.5h12M5.5 2v2M10.5 2v2" stroke="currentColor" strokeWidth="1.2" />
        </svg>
        <span>{formatRange(range)}</span>
        {compare !== 'none' ? (
          <span className="date-range__compare-chip">vs {COMPARE_LABELS[compare].toLowerCase()}</span>
        ) : null}
      </button>

      {open ? (
        <>
          <div className="popover-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="date-range__panel" role="dialog" aria-label="Volba období">
            <div className="date-range__section">
              <div className="date-range__section-title">Období</div>
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`date-range__preset${activePreset === preset ? ' is-active' : ''}`}
                  onClick={() => onChange(presetRange(preset), compare)}
                >
                  <span className="date-range__check" aria-hidden="true">
                    {activePreset === preset ? '✓' : ''}
                  </span>
                  {PRESET_LABELS[preset]}
                </button>
              ))}
            </div>

            <div className="date-range__section date-range__section--custom">
              <div className="date-range__section-title">Vlastní rozsah</div>
              <div className="date-range__custom">
                <label>
                  <span>Od</span>
                  <input
                    type="date"
                    value={range.from}
                    max={range.to}
                    onChange={(e) => onChange({ ...range, from: e.target.value }, compare)}
                  />
                </label>
                <label>
                  <span>Do</span>
                  <input
                    type="date"
                    value={range.to}
                    min={range.from}
                    onChange={(e) => onChange({ ...range, to: e.target.value }, compare)}
                  />
                </label>
              </div>
            </div>

            <div className="date-range__section">
              <div className="date-range__section-title">Porovnat s</div>
              {(Object.keys(COMPARE_LABELS) as CompareMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`date-range__preset${compare === mode ? ' is-active' : ''}`}
                  onClick={() => onChange(range, mode)}
                >
                  <span className="date-range__check" aria-hidden="true">
                    {compare === mode ? '✓' : ''}
                  </span>
                  {COMPARE_LABELS[mode]}
                </button>
              ))}
              {comparison ? (
                <div className="date-range__comparison-hint">
                  Porovnávací období: {formatRange(comparison)}
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
